import type { OrderPriceResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import { feesAtom, paymentDetailAtom } from "Order/Atom/paymentAtom";
import { useOrderChargesSumQuery } from "Order/Modules/Payment/Api/Query/useOrderChargesSumQuery";
import type { ProgressElement } from "Order/Modules/Payment/Components/PaymentLegend";
import { useDistributionChartOptions } from "Order/Modules/Payment/Utilities/useFeeChartOptions";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { PieChart } from "Shared/Components/Charts/PieChart";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useAtom } from "jotai";

type DistributionWidgetProps = {
	orderId: number;
	currency?: string;
	country?: string;
	orderPrice: OrderPriceResponseDto;
};

const paymentResource = Resources.base.payment.shared;

export function DistributionWidget({
	orderId,
	currency,
	country,
	orderPrice,
}: DistributionWidgetProps) {
	const { t } = useResource();
	const theme = useTheme();
	const [paymentDetail] = useAtom(paymentDetailAtom);
	const [fees] = useAtom(feesAtom);
	const { data: charges } = useOrderChargesSumQuery(orderId);

	// Calculate service fee percentage
	const serviceFeeAmount = fees?.serviceFeeAmount ?? 0;
	const orderTotal = orderPrice.totalPrice || 1; // Prevent division by zero
	const serviceFeePercentage = (serviceFeeAmount / orderTotal) * 100;

	const chartElements = [
		{
			value: charges ?? 0,
			label: t(paymentResource.otherCharges),
			color: theme.palette.primary.main,
		},
		{
			value: serviceFeeAmount,
			label: t(paymentResource.serviceFeeAmount),
			color: theme.palette.primary.main,
		},
		{
			value: fees?.serviceVATAmount ?? 0,
			label: t(paymentResource.serviceVATAmount),
			color: theme.palette.primary.dark,
		},
		{
			value: paymentDetail?.partnerDue ?? 0,
			label: t(paymentResource.partnerDue),
			color: theme.palette.secondary.main,
		},
	];

	const { chartSeries, options } = useDistributionChartOptions(chartElements);

	const labeledElements: ProgressElement[] = [
		{
			value: serviceFeeAmount,
			label: `${t(paymentResource.serviceFeeAmount)} (${serviceFeePercentage.toFixed(2)} %)`,
			color: theme.palette.primary.main,
		},
		{
			value: fees?.serviceVATAmount ?? 0,
			label: `${t(paymentResource.serviceVATAmount)} (${fees?.serviceFeeVATPercentage ?? 0} %)`,
			color: theme.palette.primary.dark,
		},
		{
			value: charges ?? 0,
			label: t(paymentResource.otherCharges),
			color: theme.palette.primary.main,
		},
		{
			value: paymentDetail?.partnerDue ?? 0,
			label: t(paymentResource.partnerDue),
			color: theme.palette.secondary.main,
		},
		{
			value: paymentDetail?.customerDebt ?? 0,
			label: t(paymentResource.customerDebt),
			color: theme.palette.grey[700],
		},
	];

	return (
		<BaseCard>
			<Typography variant="h5">{t(paymentResource.distribution)}</Typography>
			<Stack rowGap={1} mt={2}>
				{labeledElements.map(({ color, label, value }, index) => (
					<Stack
						key={`${index}-${value}`}
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
					>
						<Stack flexDirection="row" alignItems="center" gap={1}>
							<Box
								sx={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									backgroundColor: color,
								}}
							/>
							<Typography variant="body1">{label}</Typography>
						</Stack>
						<Typography variant="body1">
							{formatCurrency(country, currency, value)}
						</Typography>
					</Stack>
				))}
			</Stack>
			<Box margin="auto" maxWidth="250px">
				<PieChart chartSeries={chartSeries} options={options} />
			</Box>
		</BaseCard>
	);
}
